import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { Router } from "@angular/router";
import { Subject, Observable } from "rxjs";
import { CognitoAuth } from "amazon-cognito-auth-js";
import { environment } from "../../environments/environment";
import { switchMap, first } from "rxjs/operators";
import { Location } from "@angular/common";
import { GlobalService } from '../services/global/global.service';
import { UserModel } from '../models/user.model';

@Injectable({
  providedIn: "root"
})
export class AwsAuthServiceService {
  private authData: any;
  private auth: any;
  private session: any;

  private subject = new Subject<string>();

  constructor(
    private location: Location,
    private router: Router,
    private globals: GlobalService,
    private http: HttpClient
  ) {
    // To avoid ReferenceError: global is not defined
    (window as any).global = window;
  }

  public setAuthInstance() {
    // Set the authData
    this.authData = environment.aws.cognito;
    this.auth = new CognitoAuth(this.authData);

    // https://lwasantospoc.auth.ap-southeast-2.amazoncognito.com/login?redirect_uri=http%3A%2F%2Flocalhost%3A4200%2F&response_type=code&client_id=k3ku2rg206por97674qme3mmg&state=1fLMz484gcliBjn57ItWVdK4TvRXA5b3&scope=email%20openid&identity_provider=xyz
    // http://localhost:4200/?error_description=unauthorized_client&state=QikRAtt5dwCrOtX7PpUI7g6Dwof9FQFW&error=invalid_request
    // The default response_type is "token", uncomment the next line will make it be "code".
    this.auth.useCodeGrantFlow();

    // Check for set user auth (if any)
    this.updateAuthParams();

    this.auth.userhandler = {
      // user signed in
      onSuccess: session => {
        this.signedIn(session);
      },
      onFailure: err => {
        console.log("Error in cognito hosted auth response", err);
        this.onFailureMethod();
      }
    };

    // Get cached session on refresh
    if (this.isAuthenticated) this.getSession();
  }

  public get isAuthenticated(): boolean {
    return this.auth.isUserSignedIn();
  }

  public get authenticatedUser() {
    // gets the current user from the local storage
    return this.auth.getCurrentUser();
  }

  public acquireToken(): Observable<any> {
    setTimeout(() => {
      this.getSession();
    });
    return this.subject.asObservable();
  }

  public getSession() {
    this.auth.getSession();
  }

  public get accessToken(): string {
    if (this.isSessionExists) return this.session.getIdToken().getJwtToken();
    return "";
  }

  public get isSessionExists(): boolean {
    return !(!this.session || (this.session && !this.session.isValid()));
  }

  public get isSessionExpired(): boolean {
    return new Date() >= new Date(this.sessionExpirationTime);
  }

  public get sessionExpirationTime(): Date {
    if (!this.isSessionExists) return null;
    return new Date(this.session.getIdToken().getExpiration() * 1000);
  }

  private signedIn(session) {
    this.session = session;

    // Pass access token
    this.subject.next(this.accessToken);
    // this.subject.complete();
    //this.updateAuthParams();

    const returnUrl = localStorage.getItem("returnUrl");
    if (returnUrl) {
      this.router.navigateByUrl(returnUrl);
    }

    // Clear the login timeout once authenticated
    clearTimeout(+localStorage.getItem("loginInTimeout"));

    // Remove local storage variables (if exists)
    localStorage.removeItem("returnUrl");
    localStorage.removeItem("loginInProgress");
    localStorage.removeItem("loginInTimeout");

    //console.log("Session signed in: ", this.session);    
    let user: UserModel = new UserModel();
    user.username = this.session.getAccessToken().payload.username;
    user.username = user.username.replace('SantosPROD_SANTOS\\', '');
    this.globals.SetUser(user);
  }

  private onFailureMethod() {
    this.session = undefined;
    // Remove local storage variables (if exists)
    localStorage.removeItem("loginInProgress");
  }

  public login() {

    // this.auth.parseCognitoWebResponse(window.location.href);

    let expires: number = 15, // In seconds
      now: number = Date.now(), //millisecs since epoch time, lets deal only with integer
      schedule: number = now + expires * 1000,
      expiresIn: number = +localStorage.getItem("loginInProgress") || 0;

    // If URL login then remove login progress
    //if (window.location.href.endsWith("/login") && expiresIn < now) {
    if ((window.location.href.includes("/login") && expiresIn < now && expiresIn !== 0) ||
        (expiresIn < now && expiresIn !== 0)) {
      
      localStorage.removeItem("loginInProgress");
    }

    // If login in progress then do not login again
    if (localStorage.getItem("loginInProgress")) return;

    console.log("Authenticated: ", this.isAuthenticated);
    console.log("Session Exists: ", this.isSessionExists);

    if (expiresIn === 0) {
      // Set Login in progress
      localStorage.setItem("loginInProgress", schedule.toString());
      this.auth.parseCognitoWebResponse(window.location.href);
      //this.updateAuthParams();
      this.auth.getSession();
    }
/*
    // Set timer for previous login error expiration
    let count = (expiresIn - Date.now()) / 1000;

    // To reload the page
    
    let loginTimeout = setTimeout(() => {
      document.location.reload();
    }, count * 1000);
    
    // To clear the interval later if not required
    localStorage.setItem("loginInTimeout", loginTimeout.toString());
*/
    // this.auth.getSession();
    //this.auth.startSession(); // Start Session is not a function
  }

  public logout() {
    this.auth.signOut();
  }

  private removeURLAuthParam() {
    this.location.replaceState(this.router.url);
  }

  private updateAuthParams() {
    // Set auth params to defined storage
    const curUrl = window.location.href;
    this.auth.parseCognitoWebResponse(curUrl.replace("/#/", "/"));

    // Remove auth params from URL
    // this.removeURLAuthParam();
  }

  public get(url: string, params?: HttpParams): Observable<any> {
    return this.acquireToken().pipe(first()).pipe(
      switchMap(token =>
        this.http.get(url, {
          params: params,
          headers: {
            Authorization: "Bearer " + token,
            withCredentials: "true",
            "Content-Type": "application/json; charset=utf-8",
            "Ocp-Apim-Subscription-Key":
              environment.azurePortalConfig.subscription
          }
        })
      )
    );
  }

  public post(url: string, data: any): Observable<any> {
    return this.acquireToken().pipe(first()).pipe(
      switchMap(token =>
        this.http.post(url, data, {
          headers: {
            Authorization: "Bearer " + token,
            withCredentials: "true",
            "Content-Type": "application/json; charset=utf-8",
            "Ocp-Apim-Subscription-Key":
              environment.azurePortalConfig.subscription
          }
        })
      )
    );
  }

  public getAttachment(url: string, params?: HttpParams): Observable<any> {
    return this.acquireToken().pipe(first()).pipe(
      switchMap(token =>
        this.http.get(url, {
          params: params,
          responseType: "blob",
          headers: {
            Authorization: "Bearer " + token,
            withCredentials: "true",
            "Ocp-Apim-Subscription-Key":
              environment.azurePortalConfig.subscription
          }
        })
      )
    );
  }

  public getLoggedInUserProfilePhoto(): Observable<any> {
    return this.http.get("/assets/images/default-profile.jpg", {
      responseType: "blob",
    });
  }
}
